import React from 'react';
import { observer } from 'mobx-react';
import { TableContent } from 'libs/connection';
import { Section, SectionContent } from 'apps/shared/layout';
import { formatPrice } from 'utils/formatStrings';
import { FormStore } from 'libs/forms/app';
import { FullFormField } from 'libs/forms';
import { Button } from 'components';
import { MiscItemApp, ShipmentQuoteApp } from '../app';

interface FormData {
  formStore: FormStore;
  refs: any;
  shipmentQuoteApp: ShipmentQuoteApp;
}

type MiscData = FormData | MiscItemApp;
interface MiscProps {
  data: FormData | MiscItemApp;
}

const isMiscFormProps = (obj: MiscData): obj is FormData => {
  return (obj as FormData).formStore !== undefined;
};

function isInt(value: any): boolean {
  return (
    !Number.isNaN(value) &&
    parseInt(value, 10) === value &&
    !Number.isNaN(parseInt(value, 10))
  );
}

export const columns = [
  {
    Header: 'Name',
    accessor: '',
    component: true,
    Cell: observer(function name(miscProps: MiscProps) {
      const { data } = miscProps;
      if (isMiscFormProps(data)) {
        const { refs, formStore } = data;
        return (
          <FullFormField
            fieldStore={formStore.fieldStores.get('name')}
            refs={refs}
          />
        );
      }
      return data.name;
    }),
  },
  {
    Header: 'Price',
    accessor: '',
    component: true,
    Cell: observer(function cost(miscProps: MiscProps) {
      const { data } = miscProps;
      if (isMiscFormProps(data)) {
        const { refs, formStore } = data;
        return (
          <FullFormField
            fieldStore={formStore.fieldStores.get('cost')}
            refs={refs}
          />
        );
      }
      return <>{formatPrice(data.cost / 100.0, data.computedCurrency)}</>;
    }),
  },
  {
    Header: 'Discount',
    accessor: '',
    component: true,
    Cell: observer(function discount(miscProps: MiscProps) {
      const { data } = miscProps;
      if (isMiscFormProps(data)) {
        const { refs, formStore } = data;
        return (
          <FullFormField
            fieldStore={formStore.fieldStores.get('discount')}
            refs={refs}
          />
        );
      }
      return <>{data.discount}%</>;
    }),
  },
  {
    Header: 'Final Amount',
    component: true,
    Cell: observer(function amount(miscProps: MiscProps) {
      const { data } = miscProps;

      if (isMiscFormProps(data)) {
        const { formStore } = data;
        const { cost, discount, computedCurrency } = formStore.target;
        if (!cost || !isInt(discount)) {
          return <>--</>;
        }
        return (
          <>
            {formatPrice((cost / 100) * (1 - discount / 100), computedCurrency)}
          </>
        );
      }
      // @ts-ignore
      const { cost, discount, computedCurrency } = data;
      return (
        <>
          {formatPrice((cost / 100) * (1 - discount / 100), computedCurrency)}
        </>
      );
    }),
  },
  {
    Header: '',
    component: true,
    Cell: observer(function Actions({ data }: MiscProps) {
      if (isMiscFormProps(data)) {
        const { formStore, shipmentQuoteApp } = data;
        const { name, cost, discount } = formStore.target;

        return (
          <>
            {name && (
              <Button
                size='small'
                text='Add'
                outline
                disabled={!cost || !isInt(discount)}
                onClick={() => {
                  shipmentQuoteApp.parent.AddMiscItem();
                }}
              />
            )}
          </>
        );
      }

      const { upstream } = data;
      const shipmentQuoteApp = data.parent;

      return (
        <>
          {!upstream && (
            <Button
              text='Remove'
              outline
              size='small'
              onClick={() => {
                shipmentQuoteApp.parent.RemoveMiscItem(data);
              }}
            />
          )}
        </>
      );
    }),
  },
];

type PropsT = {
  quote: ShipmentQuoteApp;
  newMiscFormStore?: FormStore;
  disableActions?: boolean;
};

export const Miscellaneous = observer(
  ({ quote, newMiscFormStore, disableActions }: PropsT) => {
    const refs = {};
    const nodes = [...quote.miscItems];
    const finalColumns = disableActions ? columns.slice(0, -1) : columns;

    if (newMiscFormStore) {
      nodes.push({
        formStore: newMiscFormStore,
        refs,
        shipmentQuoteApp: quote,
      });
    }

    if (nodes.length === 0) {
      return null;
    }

    return (
      <Section title='Miscellaneous'>
        <SectionContent sx={{ paddingTop: 0 }}>
          <TableContent
            nodes={nodes}
            columns={finalColumns}
            loading={false}
            showEmptyPlaceholder={false}
            emptyTablePlaceholder={null}
          />
        </SectionContent>
      </Section>
    );
  },
);
